import { useMemo, useState } from "react";
import PageBanner from "@/components/PageBanner";
import { GALLERY, GALLERY_CATEGORIES } from "@/data/gallery";
import { Plus, X } from "lucide-react";

export default function Galerie() {
  const [filter, setFilter] = useState("toate");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(() => {
    return filter === "toate" ? GALLERY : GALLERY.filter((g) => g.category === filter);
  }, [filter]);

  return (
    <div data-testid="page-galerie">
      <PageBanner
        overline="Maison · Atelier"
        title="Galerie Foto"
        subtitle="Momente şi preparate surprinse în restaurantul nostru."
        testId="galerie-banner"
      />

      {/* Filter pills */}
      <div className="bg-brand-night pt-4 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div
            data-testid="gallery-filters"
            className="flex flex-wrap items-center justify-center gap-3 md:gap-4"
          >
            {GALLERY_CATEGORIES.map((c) => {
              const isActive = filter === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`gallery-filter-${c.id}`}
                  onClick={() => setFilter(c.id)}
                  className={`px-6 md:px-8 py-3 font-sans text-[11px] tracking-[0.3em] uppercase rounded-full border transition-all duration-300 ${
                    isActive
                      ? "bg-brand-gold text-brand-night border-brand-gold"
                      : "text-brand-gold border-brand-gold/60 hover:border-brand-gold hover:bg-brand-gold/10"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Masonry */}
      <div className="bg-brand-night pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div
            data-testid="gallery-grid"
            className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]"
          >
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`gallery-item-${item.id}`}
                onClick={() => setLightbox(item)}
                className="group relative w-full mb-5 block break-inside-avoid overflow-hidden border border-brand-gold/20 hover:border-brand-gold/60 transition-all duration-500"
              >
                <div
                  className={`relative w-full bg-brand-panel ${
                    item.aspect === "tall"
                      ? "aspect-[3/4]"
                      : item.aspect === "wide"
                      ? "aspect-[4/3]"
                      : "aspect-square"
                  }`}
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  {/* Fallback dark frame text */}
                  <div className="absolute inset-0 flex items-center justify-center text-center px-6 pointer-events-none">
                    <span className="opacity-0 font-serif text-brand-gold/80 italic">
                      [Foto: {item.title}]
                    </span>
                  </div>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-brand-night/65 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full border border-brand-gold flex items-center justify-center text-brand-gold">
                    <Plus size={20} strokeWidth={1.2} />
                  </div>
                  <span className="font-serif italic text-brand-gold text-lg px-4 text-center">
                    {item.title}
                  </span>
                  <span className="text-[10px] tracking-[0.35em] uppercase text-brand-ivory/70 font-sans">
                    {item.category}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {items.length === 0 && (
            <p className="text-center text-brand-ivory/60 font-serif italic py-20">
              Nu există fotografii în această categorie.
            </p>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] bg-brand-night/95 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            data-testid="gallery-lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Închide"
            className="absolute top-6 right-6 w-12 h-12 rounded-full border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-night transition-colors flex items-center justify-center"
          >
            <X size={20} strokeWidth={1.4} />
          </button>
          <div
            className="max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 border border-brand-gold/40">
              <img
                src={lightbox.src}
                alt={lightbox.title}
                className="w-full max-h-[80vh] object-contain bg-brand-deep"
              />
            </div>
            <p className="mt-4 text-center font-serif italic text-brand-gold text-lg">
              {lightbox.title}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
